// HydrationDetail — the rich "today's EHE result" surface, used inside
// HorseProfile's Today tab. Previously lived as its own page at
// /horses/:id/log/result; merged so the same content shows whether the
// user just logged (mathsOpenDefault=true) or clicked View (default=false).
//
// Owns: status hero, combined-signal alerts, recommendation + action list,
// "show the maths" dropdown.
// Doesn't own: top bar, breadcrumb, page chrome — the parent page provides.

import { useState } from "react";
import { AVATAR_TONES } from "../lib/avatarTones";
import { statusLabel } from "../lib/hydration";
import type { PaceStatus } from "../lib/hydration";
import { MOCK_YARD } from "../lib/mockData";
import type { MockHorse, MockTodayLog } from "../lib/mockData";
import "../styles/hydration-result.css";

const Chev = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface Scenario {
  drunkL: number;
  exercise: { mins: number; intensity: string | null };
  feed: { type: string; kg: number }[];
  behaviour: string;
  droppings: string;
  droppingsCount: number;
  notes: string;
}

interface EheBreakdown {
  faeces: number;
  urine: number;
  respiration: number;
  tears: number;
  sweatRest: number;
  sweatEx: number;
  totalLoss: number;
  feedWater: number;
  metabolic: number;
  drinkingNeed: number;
  deficit: number;
}

function computeEHE(horse: MockHorse, drunkL: number, scenario: Scenario): EheBreakdown {
  const w = horse.weightKg;
  const temp = MOCK_YARD.weatherC;
  const ex = scenario.exercise || { mins: 0, intensity: null };
  const feed = scenario.feed || [];

  const faeces = w * 0.018;
  const urine = w * 0.022;
  const respiration = w * 0.014 * (1 + Math.max(0, temp - 15) / 24);
  const tears = 0.8;
  const sweatRest = temp > 18 ? w * 0.004 * Math.max(0, (temp - 15) / 8) : 0;
  const intensityFactor: Record<string, number> = { Light: 0.010, Medium: 0.015, Intense: 0.023 };
  const sweatEx = (ex.mins / 60) * w * (intensityFactor[ex.intensity ?? ""] ?? 0);

  const totalLoss = faeces + urine + respiration + tears + sweatRest + sweatEx;

  const feedWaterFrac: Record<string, number> = {
    Hay: 0.12,
    Haylage: 0.45,
    Grass: 0.75,
    "Hard feed": 0.10,
  };
  const feedWater = feed.reduce((s, f) => s + f.kg * (feedWaterFrac[f.type] ?? 0.10), 0);
  const metabolic = 0.6;

  const drinkingNeed = totalLoss - feedWater - metabolic;
  const deficit = drinkingNeed - drunkL;

  const r1 = (x: number): number => Math.round(x * 10) / 10;
  return {
    faeces: r1(faeces),
    urine: r1(urine),
    respiration: r1(respiration),
    tears: r1(tears),
    sweatRest: r1(sweatRest),
    sweatEx: r1(sweatEx),
    totalLoss: r1(totalLoss),
    feedWater: r1(feedWater),
    metabolic: r1(metabolic),
    drinkingNeed: r1(drinkingNeed),
    deficit: r1(deficit),
  };
}

interface RecCopy {
  headline: string;
  rec: string;
  actions: string[];
}

function recCopy(status: PaceStatus, name: string): RecCopy {
  const table: Record<PaceStatus, RecCopy> = {
    hydrated: {
      headline: `${name} is well hydrated.`,
      rec: "Keep it up. Re-check tomorrow.",
      actions: [],
    },
    mild: {
      headline: `${name} is slightly under target.`,
      rec: "Offer fresh water and re-check this evening.",
      actions: [
        "Top up trough. Refresh if water has been standing.",
        "Re-check intake by 6pm and update today's log.",
      ],
    },
    moderate: {
      headline: `${name} is moderately dehydrated.`,
      rec: "Encourage drinking. Consider electrolytes. Re-check in 2 hours.",
      actions: [
        "Add a scoop of electrolytes to feed or a separate bucket.",
        "Check trough for blockages, taste, debris.",
        "Offer slightly warm water; often increases uptake.",
        "Re-check intake in 2 hours and update the log.",
      ],
    },
    severe: {
      headline: `${name} is significantly dehydrated.`,
      rec: "Action needed today. Add electrolytes, check trough access, and watch for early colic signs.",
      actions: [
        "Add electrolytes immediately. Offer multiple buckets.",
        "Verify trough flow + cleanliness; replace any standing water.",
        "Look for: pawing, flank-watching, rolling, restlessness.",
        "Take pulse + check gum colour. Log again in 1 hour.",
      ],
    },
    critical: {
      headline: "Call your vet.",
      rec: "This level of dehydration carries a real colic risk. Treat as an emergency.",
      actions: [
        "Call your vet now. Describe intake + behaviour + droppings.",
        "Do not force water. Offer a clean bucket and observe.",
        "Watch for colic signs: pawing, rolling, sweating, distress.",
        "Stay with the horse until the vet arrives.",
      ],
    },
  };
  return table[status];
}

interface Alert {
  tone: "severe" | "warn";
  title: string;
  body: string;
}

function combinedAlerts(scenario: Scenario, status: PaceStatus): Alert[] {
  const out: Alert[] = [];
  const droppings = scenario.droppings;
  const behaviour = scenario.behaviour;
  const waterDown = ["mild", "moderate", "severe", "critical"].includes(status);

  if (waterDown && (droppings === "None seen" || droppings === "Hard / dry")) {
    out.push({
      tone: "severe",
      title: "Droppings down + water down: classic early colic pattern.",
      body: "When both fall together, gut transit is slowing. Monitor closely: pulse, gum colour, abdominal sounds. Call your vet if behaviour worsens.",
    });
  }
  if (["mild", "moderate"].includes(status) && ["Restless", "Distressed"].includes(behaviour)) {
    out.push({
      tone: "severe",
      title: "Restless behaviour + dehydration: check for discomfort.",
      body: "Mild dehydration plus visible restlessness can be early colic discomfort. Take pulse + temp; check for flank-watching and lying down.",
    });
  }
  if (waterDown && droppings === "Soft / loose") {
    out.push({
      tone: "warn",
      title: "Loose droppings + reduced water: monitor today.",
      body: "Loose droppings increase fluid loss while intake is already low. Re-check water this afternoon; don't let it drop further.",
    });
  }
  return out;
}

function scenarioFromHorse(horse: MockHorse): Scenario {
  const today: MockTodayLog | null = horse.today;
  return {
    drunkL: horse.waterL,
    exercise: today?.exercise ?? { mins: 0, intensity: null },
    feed: today?.feed ?? [],
    behaviour: today?.behaviour ?? "Normal",
    droppings: today?.droppings ?? "Normal",
    droppingsCount: today?.droppingsCount ?? 0,
    notes: today?.notes ?? "",
  };
}

interface HydrationDetailProps {
  horse: MockHorse;
  /** True when the user just submitted a log; opens the maths section by default. */
  mathsOpenDefault?: boolean;
  /** Pre-computed status from the parent's existing pace logic; falls back to ratio. */
  statusOverride?: PaceStatus;
}

export default function HydrationDetail({
  horse,
  mathsOpenDefault = false,
  statusOverride,
}: HydrationDetailProps) {
  const [mathsOpen, setMathsOpen] = useState(mathsOpenDefault);

  const scenario = scenarioFromHorse(horse);
  const ehe = computeEHE(horse, scenario.drunkL, scenario);
  const targetL = horse.targetL;
  const drunkL = scenario.drunkL;
  const pct = Math.round((drunkL / targetL) * 100);
  const ratio = drunkL / targetL;

  let status: PaceStatus;
  if (statusOverride) {
    status = statusOverride;
  } else if (ratio >= 0.85) status = "hydrated";
  else if (ratio >= 0.65) status = "mild";
  else if (ratio >= 0.40) status = "moderate";
  else if (ratio >= 0.18) status = "severe";
  else status = "critical";

  const copy = recCopy(status, horse.name);
  const alerts = combinedAlerts(scenario, status);
  const tone = AVATAR_TONES[horse.colour] || "#5B6470";
  const barFill = Math.min(100, pct);

  const deficitLine =
    ehe.deficit > 0
      ? { label: "Deficit", value: `−${ehe.deficit}L`, cls: "is-deficit" }
      : { label: "Surplus", value: `+${Math.abs(ehe.deficit)}L`, cls: "is-surplus" };

  return (
    <>
      <section className={`hr-hero ${status}`}>
        <div className="hr-hero-body">
          <div className="hr-hero-id-block">
            <div className="hr-hero-id">
              <span className="avatar" style={{ background: tone }}>{horse.name[0]}</span>
              <span>
                <strong>{horse.name}</strong> · {horse.breed} · {horse.weightKg}kg
              </span>
            </div>
            <h1 className="hr-hero-headline">{copy.headline}</h1>
            <p className="hr-hero-subline">
              Drank <strong>{drunkL}L</strong> of an estimated <strong>{targetL}L</strong> target today
              {ratio >= 1
                ? <>. {pct}% of estimated need, above target.</>
                : <>. {pct}% of estimated need.</>}
            </p>
          </div>

          <div className="hr-hero-badge-wrap">
            <span className={`hr-status-badge ${status}`}>
              <span className={`dot ${status}`}></span>
              {statusLabel(status)}
            </span>
            <div className="hr-ratio">
              <span className="hr-ratio-actual">{drunkL}</span>
              <span className="hr-ratio-unit">L</span>
              <span className="hr-ratio-of">of</span>
              <span className="hr-ratio-target">{targetL}L</span>
            </div>
          </div>

          <div className="hr-hero-bar">
            <div className={`bar ${status}`}>
              <span style={{ width: `${barFill}%` }}></span>
            </div>
            <div className="hr-hero-bar-meta">
              <span>0L</span>
              <span>{pct}% of target</span>
              <span>{targetL}L target</span>
            </div>
          </div>
        </div>
      </section>

      {alerts.length > 0 && (
        <div className="hr-alerts">
          {alerts.map((a, i) => (
            <div key={i} className={`hr-alert ${a.tone === "warn" ? "warn" : ""}`}>
              <span className="hr-alert-flag">⚑</span>
              <div className="hr-alert-text">
                <p className="hr-alert-title">{a.title}</p>
                <p className="hr-alert-body">{a.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <section className="hr-card">
        <p className="hr-card-eyebrow">Recommendation</p>
        <p className="hr-rec-text">{copy.rec}</p>
        {copy.actions.length > 0 && (
          <ul className={`hr-rec-actions tone-${status}`}>
            {copy.actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        )}
      </section>

      <section className={`hr-maths ${mathsOpen ? "is-open" : ""}`}>
        <button className="hr-maths-head" onClick={() => setMathsOpen((o) => !o)}>
          <span className="hr-maths-head-text">
            <span className="hr-maths-head-title">Show the maths</span>
            <span className="hr-maths-head-sub">
              {ehe.totalLoss}L estimated loss · {ehe.drinkingNeed}L drinking need · {deficitLine.label.toLowerCase()} {Math.abs(ehe.deficit)}L
            </span>
          </span>
          <span className="hr-maths-chev">{Chev}</span>
        </button>

        {mathsOpen && (
          <div className="hr-maths-body">
            <div className="hr-maths-section">
              <p className="hr-maths-section-title">Estimated water loss</p>
              <div className="hr-row">
                <span className="hr-row-label">Faeces <span className="faint">~18ml/kg</span></span>
                <span className="hr-row-value">{ehe.faeces}L</span>
              </div>
              <div className="hr-row">
                <span className="hr-row-label">Urine <span className="faint">~22ml/kg</span></span>
                <span className="hr-row-value">{ehe.urine}L</span>
              </div>
              <div className="hr-row">
                <span className="hr-row-label">Respiration <span className="faint">adj. {MOCK_YARD.weatherC}°C</span></span>
                <span className="hr-row-value">{ehe.respiration}L</span>
              </div>
              <div className="hr-row">
                <span className="hr-row-label">Tears + insensible</span>
                <span className="hr-row-value">{ehe.tears}L</span>
              </div>
              {ehe.sweatRest > 0.05 && (
                <div className="hr-row">
                  <span className="hr-row-label">Sweat at rest <span className="faint">{MOCK_YARD.weatherC}°C ambient</span></span>
                  <span className="hr-row-value">{ehe.sweatRest}L</span>
                </div>
              )}
              {ehe.sweatEx > 0.05 && (
                <div className="hr-row">
                  <span className="hr-row-label">
                    Sweat during exercise <span className="faint">{scenario.exercise.mins}min {scenario.exercise.intensity?.toLowerCase()}</span>
                  </span>
                  <span className="hr-row-value">{ehe.sweatEx}L</span>
                </div>
              )}
              <div className="hr-row is-total">
                <span className="hr-row-label">Total estimated loss</span>
                <span className="hr-row-value">{ehe.totalLoss}L</span>
              </div>
            </div>

            <div className="hr-maths-section">
              <p className="hr-maths-section-title">Less: water already in</p>
              <div className="hr-row">
                <span className="hr-row-label">
                  Water from feed{" "}
                  <span className="faint">
                    {(scenario.feed || []).map((f) => `${f.kg}kg ${f.type.toLowerCase()}`).join(" + ") || "none"}
                  </span>
                </span>
                <span className="hr-row-value">−{ehe.feedWater}L</span>
              </div>
              <div className="hr-row">
                <span className="hr-row-label">Metabolic water <span className="faint">from oxidation</span></span>
                <span className="hr-row-value">−{ehe.metabolic}L</span>
              </div>
              <div className="hr-row is-total">
                <span className="hr-row-label">Today's drinking need</span>
                <span className="hr-row-value">{ehe.drinkingNeed}L</span>
              </div>
            </div>

            <div className="hr-maths-section">
              <p className="hr-maths-section-title">Today's result</p>
              <div className="hr-row">
                <span className="hr-row-label">Actual intake</span>
                <span className="hr-row-value">{drunkL}L</span>
              </div>
              <div className={`hr-row is-total ${deficitLine.cls}`}>
                <span className="hr-row-label">{deficitLine.label}</span>
                <span className="hr-row-value">{deficitLine.value}</span>
              </div>
            </div>

            <p className="hr-maths-cite">
              Calculated using the <strong>Equine Hydration Estimator</strong> algorithm
              from the University of Exeter.
            </p>
          </div>
        )}
      </section>
    </>
  );
}
